import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Delete as DeleteIcon } from '@mui/icons-material';
import {
  BelongsToSelects,
  Button,
  enqueueSnackbar,
  getIsValidAssignedTo,
  Select,
  Typography,
  YardPlaceEmployeeBlocksMultiSelect,
} from '../../design-system';
import { DashboardForm, DashboardFormCustomColumn, DashboardHeader } from '../dashboard';
import { useAddInventoryMutation, useEmployeesYardOptionsQuery } from '../../hooks';
import { getNormalFormProps, showToRoles } from '../../utils';
import { INPUT_ERRORS, MESSAGES } from '../../const';
import { i18n } from '../../i18n';
import { useStore } from '../../context';
import { User, BlockWithMinMaxOptional } from '../../types';

let formProps: any = {};

const getSubmitProps = (user: User) => {
  const validators = {
    yardId: formProps.yardId,
    assignedTo: getIsValidAssignedTo(formProps.yardId, formProps.assignedTo, user),
  };

  if ([...Object.values(validators)].some((val) => !val)) {
    return null;
  }

  delete formProps.isSubmitClicked;

  return getNormalFormProps(formProps);
};

export const InventoryAddHeader = ({ showOnlySubmit }: any) => {
  const navigate = useNavigate();
  const { mutate } = useAddInventoryMutation();
  const {
    state: {
      user: { data: user },
    },
  } = useStore();

  const handleSubmitClick = () => {
    formProps.onSubmitClick();
    const props = getSubmitProps(user);
    if (!props) return;

    mutate(props, {
      onSuccess: () => {
        enqueueSnackbar(MESSAGES.INVENTORY.SUCCESS.ADDED, 'success');
        navigate('/dashboard/inventory');
      },
      onError: () => {
        enqueueSnackbar(MESSAGES.INVENTORY.ERROR.NOT_ADDED, 'error');
      },
    });
  };

  return (
    <DashboardHeader>
      {!showOnlySubmit && (
        <>
          <Typography variant={'textXL'} weight={'bold'}>
            {i18n.t('pages.inventoryAdd.title')}
          </Typography>
          <Button size={'medium'} data-color={'white'} to={-1}>
            {i18n.t('button.goBack')}
          </Button>
        </>
      )}
      {showOnlySubmit && <span />}
      <Button size={'medium'} onClick={handleSubmitClick}>
        {i18n.t('button.submit')}
      </Button>
    </DashboardHeader>
  );
};

export const InventoryAddForm = () => {
  const {
    state: {
      user: { data: user },
    },
  } = useStore();

  const [yardId, setYardId] = useState(user.yardEmployee?.yardId || '');
  const [assignedTo, setAssignedTo] = useState<any[]>([{ userId: '', blocks: [] }]);
  const [updateKey, setUpdate] = useState<any>();

  const { data: employeesYardOptions = [] } = useEmployeesYardOptionsQuery(yardId);

  const validators = {
    assignedTo: assignedTo.map((item) => ({
      userId: formProps.isSubmitClicked ? (!item.userId ? INPUT_ERRORS.REQUIRED.DEFAULT : '') : '',
    })),
  };

  useEffect(() => {
    if (!formProps) return;

    formProps.isSubmitClicked = false;
    setUpdate(Math.random());
  }, []);

  useEffect(() => {
    formProps = {
      ...formProps,
      yardId,
      assignedTo,
      onSubmitClick: () => {
        formProps.isSubmitClicked = true;
        setUpdate(Math.random());
      },
    };
  }, [yardId, JSON.stringify(assignedTo)]);

  const handleDeleteClick = (i: number) => {
    setUpdate(Math.random());

    const filteredAssignedTo = assignedTo.filter((_: any, index: any) => i !== index);
    setAssignedTo(filteredAssignedTo);
  };

  const handlePlusClick = () => {
    setAssignedTo([...assignedTo, { userId: '', blocks: [] }]);
  };

  const onPlaceNotFound = () => {
    enqueueSnackbar(MESSAGES.YARD.ERROR.PLACE_NOT_FOUND, 'error');
    setAssignedTo([{ userId: '', blocks: [] }]);
  };

  return (
    <DashboardForm paperSize={'full-size'}>
      {showToRoles(['SUPER_ADMIN', 'YARD_COMPANY_ADMIN'], user) && (
        <BelongsToSelects
          handleYardChange={(obj) => {
            setYardId(obj.id);
            setAssignedTo([{ userId: '', blocks: [] }]);
          }}
          isSubmitClicked={formProps.isSubmitClicked}
        />
      )}
      {!!yardId &&
        assignedTo.map((item, i) => (
          <DashboardFormCustomColumn key={updateKey + i} columns={'1fr'} align={'start'}>
            <DashboardFormCustomColumn
              columns={assignedTo.length === 1 ? '1fr' : '1fr max-content'}
              align={'start'}
            >
              <Select
                id={`employee-${i}`}
                label={i18n.t('label.employee')}
                value={item.userId}
                options={employeesYardOptions.map((item) => ({
                  ...item,
                  disabled: assignedTo.some((nItem) => nItem.userId === item.id),
                }))}
                onSelect={(option: any) => {
                  setAssignedTo(
                    assignedTo.map((item, index) =>
                      i === index ? { ...item, userId: option.id } : item,
                    ),
                  );
                }}
                error={!!validators.assignedTo[i].userId}
                helperText={validators.assignedTo[i].userId}
                disabled={!yardId}
              />
              {assignedTo.length !== 1 && (
                <Button
                  className={'row-button-square'}
                  size={'medium'}
                  data-color={'destructiveSecondary'}
                  isSquare={true}
                  onClick={() => handleDeleteClick(i)}
                >
                  <DeleteIcon />
                </Button>
              )}
            </DashboardFormCustomColumn>
            <DashboardFormCustomColumn columns={'1fr'} align={'start'}>
              <YardPlaceEmployeeBlocksMultiSelect
                yardId={yardId}
                blocks={item.blocks}
                setBlocks={(blocks: BlockWithMinMaxOptional[]) => {
                  setAssignedTo(
                    assignedTo.map((item, index) => (i === index ? { ...item, blocks } : item)),
                  );
                }}
                isSubmitClicked={formProps.isSubmitClicked}
                withDefaultValues
                onNotFound={onPlaceNotFound}
              />
            </DashboardFormCustomColumn>
            {i === assignedTo.length - 1 && (
              <DashboardFormCustomColumn columns={'max-content'} align={'start'}>
                <Button size={'medium'} data-color={'secondary'} onClick={handlePlusClick}>
                  {i18n.t('button.addEmployee')}
                </Button>
              </DashboardFormCustomColumn>
            )}
          </DashboardFormCustomColumn>
        ))}
    </DashboardForm>
  );
};

// submitPROPS: {yardId, {userId, blocks[]}[]}
// mutation: useAddInventoryMutation
// InventoryAddHeader
// InventoryAddForm
